import findLast from 'lodash/findLast'
import React from 'react'
import { createPortal } from 'react-dom'
import uuid from 'uuid/v4'

import { ThemeProvider, useTheme } from './ThemeProvider'

type LayerObject = {
  id: string
  onDocumentClick?: (e: MouseEvent) => void
  onEscape?: (e: KeyboardEvent) => void
}

type UseLayerOptions = {
  isActive: boolean
  onDocumentClick?: LayerObject['onDocumentClick']
  onEscape?: LayerObject['onEscape']
}

type PersistedLayerProps = {
  children: React.ReactNode
  // NOTE(yan): We accept a string here to allow people to pass in CSS variables
  DANGEROUSLY_SET_Z_INDEX?: number | string
} & UseLayerOptions

type LayerProps = Omit<PersistedLayerProps, 'isActive'>

/**
 * Renders its children in a new layer on top of any previously created layers and listens for
 * global events that might lead to the removal of the layer — clicking the document or hitting
 * the escape key.
 *
 * These global events are only sent to the topmost layer. This means that layers
 * can be removed in order: if you have a popover inside of a modal, for example, closing the
 * popover via the escape key won't lead to the modal being closed at the same time. A user can
 * hit the escape key once to close the popover, then once again to close the modal.
 */
export function Layer({
  children,
  DANGEROUSLY_SET_Z_INDEX,
  ...layerOptions
}: LayerProps) {
  useLayerInternals({ isActive: true, ...layerOptions })
  return (
    <ThemedLayerPortal zIndex={DANGEROUSLY_SET_Z_INDEX}>
      {children}
    </ThemedLayerPortal>
  )
}

/**
 * A Persisted layer acts the same as a Layer when active. When a persisted layer is inactive,
 * it will still be in the DOM but just hidden.
 */
export function PersistedLayer(props: PersistedLayerProps) {
  const { children, DANGEROUSLY_SET_Z_INDEX, ...layerOptions } = props
  useLayerInternals(layerOptions)
  return (
    <ThemedLayerPortal
      isActive={layerOptions.isActive}
      zIndex={DANGEROUSLY_SET_Z_INDEX}
    >
      {children}
    </ThemedLayerPortal>
  )
}

function useLayerInternals(options: UseLayerOptions) {
  // NOTE(yan): layer id is only being used in callback functions
  //  and updating it should not trigger a component update
  const layerIdRef = React.useRef<string>(undefined)
  const context = useLayersContext()

  React.useEffect(() => {
    const layerId = context.addLayer(options)
    layerIdRef.current = layerId
    return () => context.removeLayer(layerId)
    // NOTE(yan): only add the layer when the component mounts and remove it
    //   when the component unmounts
    // NOTE (david.aghassi) this was disabled during the upgrade of eslint-plugin-react-hooks.
    // Or was a pre-existing offense
    // Please refactor and solve the issue next time this code is touched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const layerId = layerIdRef.current
    if (!layerId) {
      return
    }
    context.updateLayer(layerId, options)
    // NOTE(yan): only update the layer using the latest context value when
    //   the layer options changes
    // NOTE (david.aghassi) this was disabled during the upgrade of eslint-plugin-react-hooks.
    // Or was a pre-existing offense
    // Please refactor and solve the issue next time this code is touched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])
}

type ThemedLayerPortalProps = {
  children: React.ReactNode
  isActive?: boolean
  zIndex?: number | string
}

function ThemedLayerPortal(props: ThemedLayerPortalProps) {
  const { children, isActive = true, zIndex = 'auto' } = props

  const theme = useTheme()

  const container = useLayersContainer()
  if (!container) {
    return null
  }

  // NOTE(yan): We render a ThemeProvider here with the same theme we pull
  // from context in order to redefine all CSS variables at this new location
  // in the DOM
  return createPortal(
    <ThemeProvider theme={theme}>
      {({ rootClassName }) => (
        <div
          className={rootClassName}
          css={[
            !isActive && { display: 'none' },
            {
              left: 0,
              position: 'absolute',
              top: 0,
              // NOTE(yan): It's possible that this is a string to support CSS
              // variables, so we cast to any
              zIndex: zIndex as any,
            },
          ]}
        >
          {children}
        </div>
      )}
    </ThemeProvider>,
    container
  )
}

type LayersCallbackArgs = Omit<LayerObject, 'id'> & { isActive?: boolean }

type LayersContextValue = {
  addLayer: (l: LayersCallbackArgs) => string
  removeLayer: (id: string) => void
  updateLayer: (id: string, updates: Partial<LayersCallbackArgs>) => void
  container: HTMLDivElement | null
}

const LayersContext = React.createContext<LayersContextValue | null>(null)
export function LayersProvider({ children }: { children: React.ReactNode }) {
  const [layersMap, setLayersMap] = React.useState<{
    [key: string]: LayerObject
  }>({})
  // NOTE(yan): this list only tracks the ids of layers being displayed,
  //   i.e. inactive persistent layers would not be here
  const [activeLayersIdList, setActiveLayersIdList] = React.useState<
    Array<string>
  >([])

  // NOTE(yan):
  //   keep a centralized container div for all <Layer> to render it's content
  //     into instead of having each layer create their own container at mount.
  //     this way we can skip an extra update cycle for the contents of the
  //     portal to be painted to the DOM.
  //   this is for cases like with downshift's select menu (which we built our
  //     select menu on) where the other parts of the component expects the
  //     list menu (which is being displayed through the portal) to be in the
  //     dom at the same time.
  //   in the case where the <LayersProvider> is being rendered the first time
  //     along with other content that's being rendered through a <Layer>,
  //     that content will not be painted to screen until the next update,
  //     because the container ref needs an update cycle to be linked. this is
  //     a very rarely faced limitation because <LayersProvider> is expected to
  //     permanently installed near the app root and <Layer> are uses typically
  //     triggered by user interaction. but it a limitation to be aware of, and
  //     need to work around where necessary for the time being.
  //   need to useState instead of useRef since on initial render the container
  //     value will be null, so setting it should trigger an update to render
  //     contents of existing <Layer> components.
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null)

  const handleAddLayerToActiveLayersIdList = React.useCallback((id: string) => {
    setActiveLayersIdList(prev => (!prev.includes(id) ? prev.concat(id) : prev))
  }, [])
  const handleRemoveLayerFromActiveLayersIdList = React.useCallback((id: string) => {
    setActiveLayersIdList(prev =>
      prev.includes(id) ? prev.filter(layerId => layerId !== id) : prev
    )
  }, [])

  const contextValue = React.useMemo(
    () => ({
      addLayer: (newLayer: LayersCallbackArgs) => {
        const { isActive = true, ...layerCallbacks } = newLayer
        const id = uuid()
        if (isActive) {
          handleAddLayerToActiveLayersIdList(id)
        }
        setLayersMap(prev => ({
          ...prev,
          [id]: { id, ...layerCallbacks },
        }))
        return id
      },
      container,
      removeLayer: (id: string) => {
        handleRemoveLayerFromActiveLayersIdList(id)
        setLayersMap(prev => {
          const updatedMap = { ...prev }
          delete updatedMap[id]
          return updatedMap
        })
      },
      updateLayer: (id: string, updates: LayersCallbackArgs) => {
        const { isActive, ...layerUpdate } = updates
        setLayersMap(prev => ({
          ...prev,
          [id]: {
            ...prev[id],
            ...layerUpdate,
          },
        }))
        if (isActive === true) {
          handleAddLayerToActiveLayersIdList(id)
        } else if (isActive === false) {
          handleRemoveLayerFromActiveLayersIdList(id)
        }
      },
    }),
    [
      container,
      handleAddLayerToActiveLayersIdList,
      handleRemoveLayerFromActiveLayersIdList,
    ]
  )

  const topLayerIdWithDocumentClickListener = findLast(
    activeLayersIdList,
    id => !!layersMap[id]?.onDocumentClick
  )
  const topLayerWithDocumentClickListener = topLayerIdWithDocumentClickListener
    ? layersMap[topLayerIdWithDocumentClickListener]
    : undefined
  const topLayerIdWithEscapeListener = findLast(
    activeLayersIdList,
    id => !!layersMap[id]?.onEscape
  )
  const topLayerWithEscapeListener = topLayerIdWithEscapeListener
    ? layersMap[topLayerIdWithEscapeListener]
    : undefined

  React.useEffect(() => {
    function handleDocumentClick(e: MouseEvent) {
      topLayerWithDocumentClickListener?.onDocumentClick?.(e)
    }
    function handleDocumentKeyup(e: KeyboardEvent) {
      // NOTE(jare): Internet Explorer + Edge use "Esc" here
      if (e.key === 'Escape' || e.key === 'Esc') {
        // NOTE(yan): when the focus is on any <input> or <textarea> element
        //  within a <Layer> do NOT dismiss that <Layer>. instead, blur the
        //  focused element, and have the user press ESC key again to dismiss
        //  the layer.
        if (
          (document.activeElement instanceof HTMLInputElement ||
            document.activeElement instanceof HTMLTextAreaElement) &&
          document.activeElement === e.target
        ) {
          document.activeElement.blur()
          return
        }
        topLayerWithEscapeListener?.onEscape?.(e)
      }
    }
    // NOTE(yan):
    // delay registering click event when adding a new layer to prevent
    //   the click event that opened the layer to get picked up by
    //   this handler and dismiss that layer right after.
    // this is an issue in React 17 where the event bubbling seems to
    //   occur after/during the `useEffect` run, whereas in 16 it
    //   occur before the `useEffect` so this wasn't an issue.
    setTimeout(() => {
      document.addEventListener('click', handleDocumentClick)
    })
    document.addEventListener('keyup', handleDocumentKeyup)
    return () => {
      // NOTE(omer.demirkan):
      // when a click adds a new layer, that layer is added before the
      //   the document click event is received by the previous layer's
      //   document handler that would dismiss the previous layer.
      // and because that new layer added, the cleanup code for the previous
      //   layer runs and remove the document event handler, so it would
      //   never receive the event to be dismissed.
      // so push the cleanup by 1 event loop to give a chance for the previous
      //   layer to receive the document click event and dismiss itself
      // this ensures that it's possible to click back and forth between two
      //   buttons that each open a different non-overlapping layer, and correctly
      //   have each click open a new layer and close the old one
      setTimeout(() => {
        document.removeEventListener('click', handleDocumentClick)
      })
      document.removeEventListener('keyup', handleDocumentKeyup)
    }
  }, [topLayerWithDocumentClickListener, topLayerWithEscapeListener])

  return (
    <LayersContext.Provider value={contextValue}>
      {children}
      <div ref={setContainer} />
    </LayersContext.Provider>
  )
}

function useLayersContext(): LayersContextValue {
  const value = React.useContext(LayersContext)
  if (!value) {
    throw new Error(
      'This component must be rendered inside of a <LayersProvider>'
    )
  }
  return value
}

function useLayersContainer(): LayersContextValue['container'] {
  return useLayersContext().container
}
